import { memo } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { blue, grey } from "@mui/material/colors";
import { IZNSCampaignCondition } from "modules/outbound-message/store/znsCampaignSlice";
import ToolbarDateRange from "components/Common/ToolbarDateRange";
import { ConditionRow } from "modules/outbound-message/types/znsCampaign";

export interface DisplayConditionConfig extends IZNSCampaignCondition {
  operator: string;
  value: string;
  onChange: (value: string) => void;
  campaignData: any[];
}

export type FieldOption = { fieldName: string; displayName: string };
export type FieldConfigMap = Record<string, DisplayConditionConfig>;

const MAX_LENGTH = 3;
const MAX = 365;

const OperatorSelect = ({
  value,
  onChange,
  sx,
}: {
  value: "AND" | "OR";
  onChange: (val: "AND" | "OR") => void;
  sx?: any;
}) => (
  <Select
    size="small"
    value={value}
    onChange={(e) => onChange(e.target.value as "AND" | "OR")}
    sx={{
      backgroundColor: grey[200],
      borderRadius: 1,
      "& .MuiSelect-select": {
        py: 0.75,
        px: 1.5,
        fontSize: "0.8rem",
        fontWeight: "bold",
        color: grey[700],
      },
      "&:before, &:after": {
        display: "none",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
      boxShadow: "0px 1px 2px rgba(0,0,0,0.1)",
      "&:hover": {
        backgroundColor: grey[300],
      },
      ...sx,
    }}
  >
    <MenuItem value="AND">AND</MenuItem>
    <MenuItem value="OR">OR</MenuItem>
  </Select>
);

export interface CampaignConfig {
  id: string;
  campaignId: string;
  campaignName?: string;
  conditions: ConditionRow[];
  logicalOperator?: "AND" | "OR";
}

const ConditionValueInput = memo(function ConditionValueInput({
  valueType,
  enumValues,
  value,
  onChange,
  operator,
  campaignData,
}: DisplayConditionConfig) {
  switch (valueType) {
    case "enum":
      return (
        <FormControl variant="standard" sx={{ minWidth: 120 }}>
          <InputLabel>Value</InputLabel>
          <Select value={value} onChange={(e) => onChange(e.target.value)}>
            {enumValues?.map((enumValue) => (
              <MenuItem key={enumValue.value} value={enumValue.value}>
                {enumValue.displayName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    case "string":
      return (
        <TextField
          value={value}
          variant="standard"
          size="small"
          label="Value"
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "number":
      return (
        <TextField
          value={value}
          label="Value"
          type="number"
          variant="standard"
          size="small"
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "boolean":
      return (
        <FormControl variant="standard" sx={{ minWidth: 120 }}>
          <InputLabel>Value</InputLabel>
          <Select value={value} onChange={(e) => onChange(e.target.value)}>
            <MenuItem value="true">True</MenuItem>
            <MenuItem value="false">False</MenuItem>
          </Select>
        </FormControl>
      );
    case "date":
      if (operator === "ISSUED_WITHIN_DAYS") {
        return (
          <TextField
            value={value}
            label="Value"
            variant="standard"
            size="small"
            type="text"
            InputLabelProps={{ shrink: true }}
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              maxLength: MAX_LENGTH,
            }}
            onChange={(e) => {
              const val = e.target.value;
              if (!/^\d*$/.test(val) || val.length > MAX_LENGTH) return;
              if (val === "") {
                onChange(val);
                return;
              }
              if (Number(val) <= MAX) {
                onChange(val);
              }
            }}
            onBlur={() => {
              if (value && Number(value) > MAX) {
                onChange(String(MAX));
              }
            }}
          />
        );
      }
      return (
        <ToolbarDateRange
          label="Value"
          value={value}
          onChange={(newValue: string) => onChange(newValue)}
        />
      );
    case "campaignData":
      if (!campaignData) return null;
      return (
        <FormControl variant="standard" sx={{ minWidth: 120 }}>
          <InputLabel>Value</InputLabel>
          <Select value={value} onChange={(e) => onChange(e.target.value)}>
            {campaignData.map((campaign) => (
              <MenuItem key={campaign.id} value={campaign.id}>
                {campaign.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    default:
      return null;
  }
});

const SendConditionRow = memo(function SendConditionRow({
  condition,
  campaignIndex,
  conditionIndex,
  showConditionOperator,
  fieldOptions,
  fieldConfigMap,
  campaignSelectedData,
  onLogicalOperatorChange,
  onFieldNameChange,
  onValueChange,
  onRemove,
}: {
  condition: ConditionRow;
  campaignIndex: number;
  conditionIndex: number;
  showConditionOperator: boolean;
  fieldOptions: FieldOption[];
  fieldConfigMap: FieldConfigMap;
  campaignSelectedData: any[];
  onLogicalOperatorChange: (
    campaignIndex: number,
    conditionIndex: number,
    val: "AND" | "OR",
  ) => void;
  onFieldNameChange: (
    campaignIndex: number,
    conditionIndex: number,
    fieldName: string,
  ) => void;
  onValueChange: (
    campaignIndex: number,
    conditionIndex: number,
    value: string,
  ) => void;
  onRemove: (campaignIndex: number, conditionIndex: number) => void;
}) {
  const fieldConfig = fieldConfigMap[condition.fieldName];
  const operator = fieldConfig?.operator || "EQUALS";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        backgroundColor: blue[50],
        p: 2,
        borderRadius: 1,
        minWidth: "45%",
      }}
    >
      {showConditionOperator && (
        <OperatorSelect
          value={condition.logicalOperator || "OR"}
          onChange={(val) =>
            onLogicalOperatorChange(campaignIndex, conditionIndex, val)
          }
          sx={{ mr: 1 }}
        />
      )}
      <FormControl variant="standard" sx={{ minWidth: 120 }}>
        <InputLabel>Condition</InputLabel>
        <Select
          value={condition.fieldName}
          onChange={(e) =>
            onFieldNameChange(campaignIndex, conditionIndex, e.target.value)
          }
        >
          {fieldOptions.map((opt) => (
            <MenuItem key={opt.fieldName} value={opt.fieldName}>
              {opt.displayName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {fieldConfig ? (
        <ConditionValueInput
          {...fieldConfig}
          value={condition.value || ""}
          operator={operator}
          campaignData={campaignSelectedData}
          onChange={(value) =>
            onValueChange(campaignIndex, conditionIndex, value)
          }
        />
      ) : null}
      <IconButton
        size="small"
        aria-label="Remove condition"
        onClick={() => onRemove(campaignIndex, conditionIndex)}
        sx={{ color: grey[600] }}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Box>
  );
});

const SendCampaignSection = memo(function SendCampaignSection({
  config,
  campaignIndex,
  showBetweenCampaignOperator,
  fieldOptions,
  fieldConfigMap,
  campaignSelectedData,
  onOpenSelectCampaign,
  onCampaignLogicalOperatorChange,
  onAddCondition,
  onLogicalOperatorChange,
  onFieldNameChange,
  onValueChange,
  onRemove,
  canRemoveCampaign,
  onRemoveCampaign,
}: {
  config: CampaignConfig;
  campaignIndex: number;
  showBetweenCampaignOperator: boolean;
  fieldOptions: FieldOption[];
  fieldConfigMap: FieldConfigMap;
  campaignSelectedData: any[];
  onOpenSelectCampaign: (campaignIndex: number) => void;
  onCampaignLogicalOperatorChange: (
    campaignIndex: number,
    val: "AND" | "OR",
  ) => void;
  onAddCondition: (campaignIndex: number) => void;
  onLogicalOperatorChange: (
    campaignIndex: number,
    conditionIndex: number,
    val: "AND" | "OR",
  ) => void;
  onFieldNameChange: (
    campaignIndex: number,
    conditionIndex: number,
    fieldName: string,
  ) => void;
  onValueChange: (
    campaignIndex: number,
    conditionIndex: number,
    value: string,
  ) => void;
  onRemove: (campaignIndex: number, conditionIndex: number) => void;
  canRemoveCampaign: boolean;
  onRemoveCampaign: (campaignIndex: number) => void;
}) {
  return (
    <>
      {showBetweenCampaignOperator && (
        <Box sx={{ mb: 2, width: "fit-content" }}>
          <OperatorSelect
            value={config.logicalOperator || "OR"}
            onChange={(val) =>
              onCampaignLogicalOperatorChange(campaignIndex, val)
            }
          />
        </Box>
      )}
      <Box
        sx={{
          border: `1px solid ${grey[300]}`,
          borderRadius: 1,
          p: 2,
          mb: 2,
          position: "relative",
        }}
      >
        {canRemoveCampaign && (
          <IconButton
            size="small"
            aria-label="Remove campaign"
            onClick={() => onRemoveCampaign(campaignIndex)}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              color: grey[600],
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}
        <FormControl
          fullWidth
          variant="standard"
          sx={{ mb: 2, pr: canRemoveCampaign ? 4 : 0 }}
        >
          <InputLabel shrink>OL Campaign</InputLabel>
          <Select
            value={config.campaignId || ""}
            onClick={(e) => {
              e.preventDefault();
              onOpenSelectCampaign(campaignIndex);
            }}
            open={false}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Typography color="textSecondary">Select Campaign</Typography>
                );
              }
              return config.campaignName || selected;
            }}
          >
            {config.campaignId && (
              <MenuItem value={config.campaignId}>
                {config.campaignName || config.campaignId}
              </MenuItem>
            )}
          </Select>
        </FormControl>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {config.conditions.map((condition, conditionIndex) => (
            <SendConditionRow
              key={condition.id}
              condition={condition}
              campaignIndex={campaignIndex}
              conditionIndex={conditionIndex}
              showConditionOperator={conditionIndex > 0}
              fieldOptions={fieldOptions}
              fieldConfigMap={fieldConfigMap}
              campaignSelectedData={campaignSelectedData}
              onLogicalOperatorChange={onLogicalOperatorChange}
              onFieldNameChange={onFieldNameChange}
              onValueChange={onValueChange}
              onRemove={onRemove}
            />
          ))}

          <Box
            onClick={() => onAddCondition(campaignIndex)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1px dashed ${blue[300]}`,
              borderRadius: 1,
              p: 2,
              minWidth: "45%",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: blue[50],
              },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <AddIcon sx={{ color: blue[600], fontSize: 20 }} />
              <Typography
                variant="button"
                sx={{ color: blue[600], fontWeight: "bold" }}
              >
                CONDITION
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Box>
    </>
  );
});

export default SendCampaignSection;
